import React, { useState } from "react";
import { Heart, Send, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function ContactSection() {
  const [kind, setKind] = useState("contact");
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", body: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/messages", { ...form, kind });
      toast.success(
        kind === "prayer" ? "Demande de prière envoyée" : "Message envoyé",
        { description: "Nous vous répondrons dans les plus brefs délais." }
      );
      setForm({ name: "", email: "", phone: "", subject: "", body: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Impossible d'envoyer le message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 text-gold-dark text-xs tracking-[0.3em] uppercase mb-4">
            <Heart className="w-3.5 h-3.5" /> Restons en lien
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-deepblue-900 font-semibold leading-tight">
            Contact & prière
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Une question, une demande de prière, un témoignage à partager ? Écrivez-nous.
          </p>
        </div>

        <div className="rounded-3xl bg-gradient-to-br from-deepblue-900 to-deepblue-800 p-8 sm:p-12 shadow-2xl">
          <Tabs value={kind} onValueChange={setKind}>
            <TabsList className="bg-white/10 mb-8" data-testid="contact-tabs">
              <TabsTrigger value="contact" data-testid="tab-contact" className="data-[state=active]:bg-gold data-[state=active]:text-white text-white/80">
                <Mail className="w-4 h-4 mr-2" /> Contact
              </TabsTrigger>
              <TabsTrigger value="prayer" data-testid="tab-prayer" className="data-[state=active]:bg-gold data-[state=active]:text-white text-white/80">
                <Heart className="w-4 h-4 mr-2" /> Demande de prière
              </TabsTrigger>
            </TabsList>

            <TabsContent value={kind}>
              <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <Field label="Nom complet" htmlFor="name">
                    <Input id="name" required value={form.name} onChange={update("name")} placeholder="Jean Dupont" data-testid="input-name" />
                  </Field>
                  <Field label="Email" htmlFor="email">
                    <Input id="email" type="email" required value={form.email} onChange={update("email")} placeholder="jean@exemple.fr" data-testid="input-email" />
                  </Field>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <Field label="Téléphone (facultatif)" htmlFor="phone">
                    <Input id="phone" value={form.phone} onChange={update("phone")} placeholder="+33 6 12 34 56 78" data-testid="input-phone" />
                  </Field>
                  <Field label={kind === "prayer" ? "Sujet de prière" : "Sujet"} htmlFor="subject">
                    <Input id="subject" required value={form.subject} onChange={update("subject")}
                      placeholder={kind === "prayer" ? "Guérison, famille, discernement…" : "Objet de votre message"}
                      data-testid="input-subject" />
                  </Field>
                </div>

                <Field label={kind === "prayer" ? "Votre demande" : "Votre message"} htmlFor="body">
                  <Textarea id="body" required rows={6} value={form.body} onChange={update("body")}
                    placeholder={kind === "prayer" ? "Partagez ce qui pèse sur votre cœur…" : "Écrivez votre message ici…"}
                    data-testid="input-body" />
                </Field>

                <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
                  <Button type="submit" disabled={submitting} data-testid="submit-btn"
                    className="bg-gold hover:bg-gold-hover text-white rounded-full px-8 py-6 text-base w-full sm:w-auto">
                    <Send className="w-4 h-4 mr-2" />
                    {submitting ? "Envoi en cours…" : "Envoyer"}
                  </Button>
                  <p className="text-white/60 text-sm">
                    {kind === "prayer" ? "Vos demandes restent confidentielles." : "Nous répondons sous 48h."}
                  </p>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </section>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="text-white/80 text-xs uppercase tracking-widest">{label}</Label>
      <div className="mt-2 [&_input]:bg-white/95 [&_input]:border-transparent [&_input]:text-deepblue-900 [&_textarea]:bg-white/95 [&_textarea]:border-transparent [&_textarea]:text-deepblue-900">
        {children}
      </div>
    </div>
  );
}
